import type {
  RevisionDTO,
  RevisionUpdateDTO,
} from '@conversed/rest/src/contracts';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { client } from '../../api/rest/client';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../helpers/toast';
import { mainActions } from '../../redux/reducers/mainHandler';

export const useRevisionsActions = () => {
  const newDispatch = useDispatch();

  const createRevision = useCallback(
    async (revisionName: string) => {
      try {
        const response = await client.revisions.createRevision({
          body: { name: revisionName },
        });
        newDispatch(mainActions.addRevision(response.data));
        toastSuccessTranslated('settings.page.revision.manager.add.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  const deleteRevision = useCallback(
    async (revisionId: string) => {
      try {
        await client.revisions.deleteRevision({
          params: { id: revisionId },
        });
        newDispatch(mainActions.deleteRevision(revisionId));
        toastSuccessTranslated('settings.page.revision.manager.remove.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  const editRevision = useCallback(
    async (id: string, updateData: RevisionUpdateDTO) => {
      try {
        await client.revisions.updateRevision({
          body: updateData,
          params: { id },
        });
        newDispatch(mainActions.updateRevision({ id, updateData }));
        toastSuccessTranslated('settings.page.revision.manager.edit.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  const setMainRevision = useCallback(
    async (id: string, updateData: RevisionDTO) => {
      try {
        await client.revisions.setRevisionAsActive({
          params: { id },
        });
        newDispatch(mainActions.getRevisionsByMain({ id, updateData }));

        toastSuccessTranslated('settings.page.revision.manager.edit.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  const fetchRevisions = useCallback(async () => {
    try {
      const { data: revisions } = await client.revisions.getAllRevisions();
      newDispatch(mainActions.populateRevision(revisions));

      const mainRevisionsId = revisions
        ?.filter((obj: RevisionDTO) => obj.isMainRevision === true)
        .map((obj: RevisionDTO) => obj._id);

      return {
        success: true,
        revisionId: mainRevisionsId?.toString() ?? '',
      };
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
    }
  }, [newDispatch]);

  return {
    createRevision,
    editRevision,
    setMainRevision,
    deleteRevision,
    fetchRevisions,
  };
};
